import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
	getPracticeSessionItems,
	getNextPracticeQuestion,
	savePracticeAnswer,
	finishPracticeSession,
	reportQuestion,
	completeStudyPlanTask,
} from "utils/supabase";
import { consumeStudyPlanTaskLink } from "utils/studyPlanLink";
import BackdropAlert from "components/Custom/BackdropAlert.js";
import ProgresoGeneral from "components/Custom/ProgresoGeneral.js";

import { Chip, Stack, Button, CircularProgress, Paper, Typography, Box, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import {
	ScrollText,
	Stethoscope,
	ArrowRight,
	ArrowLeft,
	ExternalLink,
	Timer,
	CheckCircle2,
	XCircle,
	Lightbulb,
	BookOpen,
	Square,
	Flag,
} from "lucide-react";

function getDifficultyLabel(value) {
	const normalized = String(value || "").trim().toLowerCase();

	if (["easy", "facil", "fácil"].includes(normalized)) {
		return { label: "Fácil", color: "#15803d", border: "#86efac" };
	}

	if (["hard", "difficult", "dificil", "difícil"].includes(normalized)) {
		return { label: "Difícil", color: "#dc2626", border: "#fca5a5" };
	}

	return { label: "Media", color: "#b45309", border: "#fcd34d" };
}

function groupPracticeItems(items) {
	const blocksMap = new Map();

	for (const item of items || []) {
		const questionId = item?.question_id || item?.id;
		const blockId = item?.block_id || (questionId ? `q-${questionId}` : `block-${blocksMap.size}`);
		const uniqueBlockId = questionId ? `q-${questionId}` : blockId;

		if (!blocksMap.has(uniqueBlockId)) {
			blocksMap.set(uniqueBlockId, {
				blockId: uniqueBlockId,
				itemType: item?.item_type || (item?.clinical_case ? "clinical_case" : "question"),
				clinicalCase: item?.clinical_case || null,
				image_url: item?.image_url,
				image_alt_text: item?.image_alt_text,
				questions: [],
			});
		}

		blocksMap.get(uniqueBlockId).questions.push(item);
	}

	return Array.from(blocksMap.values());
}

function normalizeNextPracticeQuestionItem(rawPayload) {
	if (!rawPayload || typeof rawPayload !== "object") return null;

	const payload = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
	const question = payload?.question;

	if (!question?.id) return null;

	const clinicalCase = payload?.clinical_case || null;
	const blockId = `q-${question.id}`;

	return {
		block_id: blockId,
		item_type: clinicalCase ? "clinical_case" : "question",
		clinical_case: clinicalCase,
		clinical_case_id: clinicalCase?.id || null,
		question_id: question.id,
		question_text: question.question_text,
		difficulty: question.difficulty,
		has_media: question.has_media,
		case_order: question.case_order,
		is_clinical_case_question: question.is_clinical_case_question,
		source: question.source,
		source_url: question.source_url,
		specialty_name: payload?.specialty?.name || null,
		topic_name: payload?.topic?.name || null,
		image_url: clinicalCase?.image_url || null,
		image_alt_text: clinicalCase?.image_alt_text || null,
		options: Array.isArray(payload?.options) ? payload.options : [],
		media: Array.isArray(payload?.media) ? payload.media : [],
	};
}

function appendPracticeItemToBlocks(previousBlocks, newItem) {
	const currentBlocks = Array.isArray(previousBlocks) ? previousBlocks : [];
	if (!newItem?.question_id) return currentBlocks;

	const blockId = `q-${newItem.question_id}`;
	const existingBlockIndex = currentBlocks.findIndex((block) => block.blockId === blockId);

	if (existingBlockIndex !== -1) {
		return currentBlocks;
	}

	return [
		...currentBlocks,
		{
			blockId,
			itemType: newItem.item_type || (newItem.clinical_case ? "clinical_case" : "question"),
			clinicalCase: newItem.clinical_case || null,
			image_url: newItem.image_url,
			image_alt_text: newItem.image_alt_text,
			questions: [newItem],
		},
	];
}

function findNextUnansweredBlockIndex(blocks, resultsByQuestion, currentBlockIndex = -1) {
	const safeBlocks = Array.isArray(blocks) ? blocks : [];
	for (let i = Math.max(0, currentBlockIndex + 1); i < safeBlocks.length; i++) {
		const hasUnanswered = (safeBlocks[i]?.questions || []).some(
			(question) => !resultsByQuestion[question.question_id]
		);

		if (hasUnanswered) return i;
	}

	return -1;
}

function normalizeAnswerResult(raw, fallbackQuestion) {
	const payload = Array.isArray(raw) ? raw[0] : raw;
	const isCorrect = Boolean(
		payload?.is_correct ?? payload?.correct ?? payload?.status === "correct"
	);

	return {
		isCorrect,
		status: payload?.status || (isCorrect ? "correct" : "incorrect"),
		timeSpentSeconds:
			payload?.time_spent_seconds ??
			payload?.timeSpentSeconds ??
			fallbackQuestion?.time_spent_seconds ??
			null,
		correctOptionId:
			payload?.correct_option_id ||
			payload?.correct_answer_option_id ||
			fallbackQuestion?.correct_option_id ||
			null,
		explanation: payload?.explanation || fallbackQuestion?.explanation || null,
		source: payload?.source || fallbackQuestion?.source || null,
		sourceUrl: payload?.source_url || fallbackQuestion?.source_url || null,
	};
}

function normalizeAnswerStatus(result) {
	const status = String(result?.status || "").trim().toLowerCase();

	if (result?.isCorrect === true || status === "correct" || status === "right") {
		return "correct";
	}

	if (
		result?.isCorrect === false ||
		status === "incorrect" ||
		status === "wrong" ||
		status === "failed"
	) {
		return "incorrect";
	}

	return "other";
}

function getSessionMeta(data) {
	if (!data) return {};

	const rows = Array.isArray(data) ? data : [data];
	const firstRow = rows[0] || {};
	const sessionInfo =
		firstRow?.session ||
		firstRow?.session_info ||
		firstRow?.study_session ||
		firstRow?.summary ||
		data?.session ||
		data?.session_info ||
		data?.study_session ||
		data?.summary ||
		{};

	// get_practice_session_items nests the flag under session.settings.is_infinite, not on the session itself.
	const settings = sessionInfo?.settings || firstRow?.settings || data?.settings || {};

	const explicitTotalQuestions =
		sessionInfo?.total_questions ??
		firstRow?.total_questions ??
		data?.total_questions ??
		null;

	const rawIsInfinite = Boolean(
		settings?.is_infinite ??
		sessionInfo?.is_infinite ??
		firstRow?.is_infinite ??
		data?.is_infinite ??
		Number(settings?.requested_total_questions ?? explicitTotalQuestions) === 0
	);

	return {
		...sessionInfo,
		is_infinite: rawIsInfinite,
		total_questions: Number(explicitTotalQuestions ?? rows.length),
	};
}

function extractPracticeItems(data) {
	return Array.isArray(data) ? data : data?.items || data?.questions || data?.session_items || [];
}

function isPracticeSessionCompleted(meta) {
	if (!meta || typeof meta !== "object") return false;

	const status = String(meta?.status || meta?.session_status || "").toLowerCase();
	return Boolean(
		meta?.is_completed ||
		status === "completed" ||
		status === "finished" ||
		meta?.completed_at ||
		meta?.finished_at ||
		meta?.ended_at
	);
}

function buildAnswerStateFromItems(items) {
	const answersMap = {};
	const initialResults = {};

	(items || []).forEach((item) => {
		if (item?.selected_option_id) {
			answersMap[item.question_id] = item.selected_option_id;

			if (item?.status === "correct" || item?.status === "incorrect") {
				initialResults[item.question_id] = normalizeAnswerResult(
					{
						status: item.status,
						is_correct: item.status === "correct",
						correct_option_id: item.correct_option_id,
						explanation: item.explanation,
						source: item.source,
						source_url: item.source_url,
					},
					item
				);
			}
		}
	});

	return { answersMap, initialResults };
}

function formatElapsedTime(totalSeconds) {
	const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
	const hours = Math.floor(safeSeconds / 3600);
	const minutes = Math.floor((safeSeconds % 3600) / 60);
	const seconds = safeSeconds % 60;

	if (hours > 0) {
		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
	}

	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function capitalizeFirstLetter(value) {
	const text = String(value || "");
	return text.replace(/^(\s*)(\S)/, (_, whitespace, firstLetter) => (
		whitespace + firstLetter.toLocaleUpperCase("es-MX")
	));
}

function resolveBlockImage(block) {
	if (!block) return null;

	const questions = block.questions || [];
	const questionWithMedia = questions.find((question) =>
		question?.media?.some((media) => media?.media_type === "image" && media?.base64_data)
	);
	const questionWithImageUrl = questions.find((question) => question?.image_url);
	const mediaImage = questionWithMedia?.media?.find(
		(media) => media?.media_type === "image" && media?.base64_data
	);

	const rawUrl = questionWithImageUrl?.image_url || block.clinicalCase?.image_url || null;
	const imageUrl =
		typeof rawUrl === "string" &&
		rawUrl.trim() &&
		rawUrl.trim().toLowerCase() !== "null" &&
		rawUrl.trim().toLowerCase() !== "undefined"
			? rawUrl.trim()
			: null;

	const imageAlt =
		mediaImage?.alt_text ||
		questionWithMedia?.image_alt_text ||
		questionWithImageUrl?.image_alt_text ||
		block.clinicalCase?.image_alt_text ||
		"Imagen del caso clinico";

	if (imageUrl) {
		return {
			src: imageUrl,
			alt: imageAlt,
		};
	}

	const rawBase64 = mediaImage?.base64_data || block.clinicalCase?.image_base64;
	if (typeof rawBase64 !== "string" || !rawBase64.trim()) {
		return null;
	}

	const base64Value = rawBase64.trim();
	if (base64Value.toLowerCase() === "null" || base64Value.toLowerCase() === "undefined") {
		return null;
	}

	const cleanBase64 = base64Value.replace(/\s+/g, "");
	if (cleanBase64.length < 32) {
		return null;
	}

	if (!/^[A-Za-z0-9+/=]+$/.test(cleanBase64)) {
		return null;
	}

	const mimeType = mediaImage?.mime_type || (cleanBase64.startsWith("PHN2Zy") ? "image/svg+xml" : "image/png");

	return {
		src: `data:${mimeType};base64,${cleanBase64}`,
		alt: imageAlt,
	};
}

export default function SesionPractica() {
	const [preguntas, setPreguntas] = useState(null);
	const [bloquActual, setBloquActual] = useState(0);
	const [respuestasSeleccionadas, setRespuestasSeleccionadas] = useState({});
	const [preguntasMarcadas, setPreguntasMarcadas] = useState({});
	const [hoverOption, setHoverOption] = useState(null);
	const [isFinishing, setIsFinishing] = useState(false);
	const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);
	const [isLoadingSession, setIsLoadingSession] = useState(true);
	const [isLoadingMoreItems, setIsLoadingMoreItems] = useState(false);
	const [paginaNavegador, setPaginaNavegador] = useState(0);
	const [questionCursorByBlock, setQuestionCursorByBlock] = useState({});
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [sessionMeta, setSessionMeta] = useState({ is_infinite: false, total_questions: 0 });
	const [answerResults, setAnswerResults] = useState({});
	const [brokenImagesByBlock, setBrokenImagesByBlock] = useState({});
	const [imageAspectRatioByBlock, setImageAspectRatioByBlock] = useState({});
	const [reportTarget, setReportTarget] = useState(null);
	const [reportComment, setReportComment] = useState("");
	const [isSubmittingReport, setIsSubmittingReport] = useState(false);
	const [preguntasReportadas, setPreguntasReportadas] = useState({});
	const [backdropAlert, setBackdropAlert] = useState({
		open: false,
		severity: "info",
		title: "",
		message: "",
		description: "",
		closeOnBackdropClick: true,
		primaryAction: null,
		secondaryAction: null,
	});

	const { sessionId } = useParams();
	const history = useHistory();
	const location = useLocation();
	const preguntasRef = useRef(null);
	const finishingRef = useRef(false);
	const questionTimersRef = useRef({});
	const sessionStartAtRef = useRef(Date.now());
	const sessionPositionStorageKey = `practice_session_position_${sessionId}`;
	const sessionTimerStorageKey = `practice_session_started_at_${sessionId}`;
	const sessionAnswersStorageKey = `practice_session_answers_${sessionId}`;
	const sessionResultsStorageKey = `practice_session_results_${sessionId}`;

	const questionById = useMemo(() => {
		const map = new Map();
		(preguntas || []).forEach((block) => {
			block.questions.forEach((q) => {
				map.set(q.question_id, q);
			});
		});
		return map;
	}, [preguntas]);

	const requestedInfiniteFromNavigation = location?.state?.isInfinite === true;
	const requestedInfiniteFromUrl = useMemo(() => {
		try {
			return new URLSearchParams(location?.search || "").get("infinite") === "1";
		} catch (error) {
			return false;
		}
	}, [location?.search]);
	const requestedInfiniteFromStorage = useMemo(() => {
		try {
			const sessionStorageValue = sessionStorage.getItem(`practice_session_infinite_${sessionId}`);
			if (sessionStorageValue === "1") return true;
			const localStorageValue = localStorage.getItem(`practice_session_infinite_${sessionId}`);
			return localStorageValue === "1";
		} catch (error) {
			return false;
		}
	}, [sessionId]);
	const isInfinite = Boolean(
		sessionMeta?.is_infinite ||
		requestedInfiniteFromNavigation ||
		requestedInfiniteFromUrl ||
		requestedInfiniteFromStorage
	);

	const flattenedQuestions = useMemo(() => {
		if (!preguntas) return [];

		const list = [];
		preguntas.forEach((block, blockIndex) => {
			(block.questions || []).forEach((question, questionIndexInsideBlock) => {
				list.push({
					question,
					blockIndex,
					questionIndexInsideBlock,
				});
			});
		});

		return list;
	}, [preguntas]);

	const currentBlock = preguntas?.[bloquActual];
	const currentQuestionIndexInsideBlock = useMemo(() => {
		if (!currentBlock) return 0;

		const blockKey = currentBlock.blockId || `block-${bloquActual}`;
		const savedIndex = questionCursorByBlock[blockKey];
		if (
			Number.isInteger(savedIndex) &&
			savedIndex >= 0 &&
			savedIndex < (currentBlock.questions?.length || 0)
		) {
			return savedIndex;
		}

		const firstUnansweredIndex = (currentBlock.questions || []).findIndex(
			(question) => !answerResults[question.question_id]
		);

		if (firstUnansweredIndex >= 0) return firstUnansweredIndex;

		return Math.max(0, (currentBlock.questions?.length || 1) - 1);
	}, [currentBlock, bloquActual, questionCursorByBlock, answerResults]);
	const currentQuestion = currentBlock?.questions?.[currentQuestionIndexInsideBlock] || null;
	const isCurrentQuestionSelected = Boolean(
		currentQuestion && respuestasSeleccionadas[currentQuestion.question_id]
	);
	const currentQuestionNumber = useMemo(() => {
		if (!preguntas || !preguntas[bloquActual]) return 0;

		const previousQuestionsCount = preguntas
			.slice(0, bloquActual)
			.reduce((acc, block) => acc + (block.questions?.length || 0), 0);

		return previousQuestionsCount + currentQuestionIndexInsideBlock + 1;
	}, [preguntas, bloquActual, currentQuestionIndexInsideBlock]);

	const showBackdropAlert = useCallback(
		({
			severity = "info",
			title = "",
			message = "",
			description = "",
			closeOnBackdropClick = true,
			primaryAction = null,
			secondaryAction = null,
		}) => {
			setBackdropAlert({
				open: true,
				severity,
				title,
				message,
				description,
				closeOnBackdropClick,
				primaryAction,
				secondaryAction,
			});
		},
		[]
	);

	const closeBackdropAlert = useCallback(() => {
		setBackdropAlert((prev) => ({ ...prev, open: false }));
	}, []);

	const getUnansweredQuestionsCount = useCallback(() => {
		if (!Array.isArray(preguntas) || preguntas.length === 0) return 0;

		let totalQuestions = 0;
		let answeredQuestions = 0;

		preguntas.forEach((block) => {
			(block?.questions || []).forEach((question) => {
				totalQuestions += 1;
				if (answerResults[question.question_id]) {
					answeredQuestions += 1;
				}
			});
		});

		return Math.max(0, totalQuestions - answeredQuestions);
	}, [preguntas, answerResults]);

	const goToFirstUnansweredQuestion = useCallback(() => {
		const firstUnansweredIndex = flattenedQuestions.findIndex(
			(item) => !answerResults[item?.question?.question_id]
		);

		if (firstUnansweredIndex < 0) return;

		const firstUnansweredItem = flattenedQuestions[firstUnansweredIndex];
		if (!firstUnansweredItem) return;

		setBloquActual(firstUnansweredItem.blockIndex);

		const targetBlock = preguntas?.[firstUnansweredItem.blockIndex];
		if (targetBlock) {
			const blockKey = targetBlock.blockId || `block-${firstUnansweredItem.blockIndex}`;
			setQuestionCursorByBlock((prev) => ({
				...prev,
				[blockKey]: firstUnansweredItem.questionIndexInsideBlock,
			}));
		}

		setPaginaNavegador(Math.floor(firstUnansweredIndex / 28));
	}, [flattenedQuestions, answerResults, preguntas]);

	const finishSessionNow = useCallback(async () => {
		if (finishingRef.current) return;

		try {
			finishingRef.current = true;
			setIsFinishing(true);

			const { error } = await finishPracticeSession(sessionId);
			if (error) throw error;

			const linkedTaskId = consumeStudyPlanTaskLink("practice", sessionId);
			if (linkedTaskId) {
				const { error: completeError } = await completeStudyPlanTask(linkedTaskId, { study_session_id: sessionId });
				if (completeError) console.error("Error completing study plan task (practice):", completeError);
			}

			try {
				sessionStorage.removeItem(sessionPositionStorageKey);
				sessionStorage.removeItem(sessionTimerStorageKey);
				sessionStorage.removeItem(sessionAnswersStorageKey);
				sessionStorage.removeItem(sessionResultsStorageKey);
			} catch (storageError) {
				console.warn("No se pudo limpiar estado local de la sesión.", storageError);
			}

			history.push(`/admin/practica/${sessionId}/resultados`);
		} catch (error) {
			console.error("Error finishing practice session:", error);
			showBackdropAlert({
				severity: "error",
				title: "No se pudo finalizar",
				message: "No se pudo finalizar la sesión de práctica.",
				description: "Por favor intenta nuevamente.",
				primaryAction: {
					label: "Aceptar",
					onClick: closeBackdropAlert,
				},
			});
			finishingRef.current = false;
		} finally {
			setIsFinishing(false);
		}
	}, [
		sessionId,
		history,
		showBackdropAlert,
		closeBackdropAlert,
		sessionPositionStorageKey,
		sessionTimerStorageKey,
		sessionAnswersStorageKey,
		sessionResultsStorageKey,
	]);

	const handleFinishSession = useCallback(() => {
		const unansweredCount = getUnansweredQuestionsCount();

		if (unansweredCount > 0) {
			showBackdropAlert({
				severity: "warning",
				title: "Tienes preguntas sin responder",
				message: `Aún tienes ${unansweredCount} pregunta${unansweredCount === 1 ? "" : "s"} sin responder.`,
				description: "Si finalizas ahora, esas preguntas se tomarán como sin responder en tus resultados.",
				closeOnBackdropClick: false,
				primaryAction: {
					label: "Finalizar de todos modos",
					onClick: () => {
						closeBackdropAlert();
						finishSessionNow();
					},
				},
				secondaryAction: {
					label: "Volver a responder",
					onClick: () => {
						closeBackdropAlert();
						goToFirstUnansweredQuestion();
					},
				},
			});
			return;
		}

		finishSessionNow();
	}, [
		getUnansweredQuestionsCount,
		showBackdropAlert,
		closeBackdropAlert,
		finishSessionNow,
		goToFirstUnansweredQuestion,
	]);

	const refreshPracticeItems = useCallback(async () => {
		const { data, error } = await getPracticeSessionItems(sessionId);
		if (error) throw error;

		const items = extractPracticeItems(data);
		const groupedQuestions = groupPracticeItems(items);
		const { answersMap, initialResults } = buildAnswerStateFromItems(items);
		const meta = getSessionMeta(data);

		setPreguntas(groupedQuestions);
		setSessionMeta(meta);
		setRespuestasSeleccionadas((prev) => ({ ...prev, ...answersMap }));
		setAnswerResults((prev) => ({ ...prev, ...initialResults }));

		return { items, groupedQuestions, meta };
	}, [sessionId]);

	useEffect(() => {
		async function loadPracticeSession() {
			setIsLoadingSession(true);

			try {
				const now = Date.now();
				let startedAt = now;

				try {
					const storedStartAt = Number(sessionStorage.getItem(sessionTimerStorageKey));
					if (Number.isFinite(storedStartAt) && storedStartAt > 0) {
						startedAt = storedStartAt;
					} else {
						sessionStorage.setItem(sessionTimerStorageKey, String(now));
					}
				} catch (storageError) {
					console.warn("No se pudo restaurar el tiempo de sesión.", storageError);
				}

				sessionStartAtRef.current = startedAt;
				setElapsedSeconds(Math.max(0, Math.floor((now - startedAt) / 1000)));

				const { items, groupedQuestions, meta } = await refreshPracticeItems();
				const { answersMap, initialResults } = buildAnswerStateFromItems(items);

				if (isPracticeSessionCompleted(meta)) {
					try {
						sessionStorage.removeItem(sessionPositionStorageKey);
						sessionStorage.removeItem(sessionTimerStorageKey);
						sessionStorage.removeItem(sessionAnswersStorageKey);
						sessionStorage.removeItem(sessionResultsStorageKey);
					} catch (storageError) {
						console.warn("No se pudo limpiar estado local de la sesión finalizada.", storageError);
					}

					history.replace(`/admin/practica/${sessionId}/resultados`);
					return;
				}

				let storedAnswers = {};
				let storedResults = {};
				try {
					storedAnswers = JSON.parse(
						sessionStorage.getItem(sessionAnswersStorageKey) || "{}"
					);
					storedResults = JSON.parse(
						sessionStorage.getItem(sessionResultsStorageKey) || "{}"
					);
				} catch (storageError) {
					console.warn("No se pudo restaurar respuestas locales de la sesión.", storageError);
				}

				setRespuestasSeleccionadas({ ...storedAnswers, ...answersMap });
				setAnswerResults({ ...storedResults, ...initialResults });

				let restoredPosition = 0;
				try {
					const storedPosition = Number(sessionStorage.getItem(sessionPositionStorageKey));
					if (Number.isInteger(storedPosition) && storedPosition >= 0) {
						restoredPosition = storedPosition;
					}
				} catch (storageError) {
					console.warn("No se pudo restaurar la posición de la sesión.", storageError);
				}

				const maxIndex = Math.max(0, (groupedQuestions?.length || 1) - 1);
				setBloquActual(Math.min(restoredPosition, maxIndex));
			} catch (error) {
				console.error("Error loading practice session:", error);
				showBackdropAlert({
					severity: "error",
					title: "Error al cargar",
					message: "No se pudo cargar la sesión de práctica.",
					description: "Intenta de nuevo en unos segundos.",
					closeOnBackdropClick: false,
					primaryAction: {
						label: "Volver",
						onClick: () => {
							closeBackdropAlert();
							history.push("/admin/practica");
						},
					},
				});
			} finally {
				setIsLoadingSession(false);
			}
		}

		loadPracticeSession();
	}, [
		sessionId,
		showBackdropAlert,
		closeBackdropAlert,
		history,
		refreshPracticeItems,
		sessionPositionStorageKey,
		sessionTimerStorageKey,
		sessionAnswersStorageKey,
		sessionResultsStorageKey,
	]);

	useEffect(() => {
		if (isLoadingSession) return;

		try {
			sessionStorage.setItem(sessionAnswersStorageKey, JSON.stringify(respuestasSeleccionadas));
		} catch (storageError) {
			console.warn("No se pudo guardar respuestas seleccionadas.", storageError);
		}
	}, [respuestasSeleccionadas, isLoadingSession, sessionAnswersStorageKey]);

	useEffect(() => {
		if (isLoadingSession) return;

		try {
			sessionStorage.setItem(sessionResultsStorageKey, JSON.stringify(answerResults));
		} catch (storageError) {
			console.warn("No se pudo guardar resultados de respuestas.", storageError);
		}
	}, [answerResults, isLoadingSession, sessionResultsStorageKey]);

	useEffect(() => {
		if (isLoadingSession) return;

		try {
			sessionStorage.setItem(sessionPositionStorageKey, String(bloquActual));
		} catch (storageError) {
			console.warn("No se pudo guardar la posición actual de la sesión.", storageError);
		}
	}, [bloquActual, isLoadingSession, sessionPositionStorageKey]);

	useEffect(() => {
		if (isLoadingSession) return;

		const intervalId = setInterval(() => {
			setElapsedSeconds(Math.floor((Date.now() - sessionStartAtRef.current) / 1000));
		}, 1000);

		return () => clearInterval(intervalId);
	}, [isLoadingSession]);

	useEffect(() => {
		if (preguntasRef.current) {
			const navbarOffset = 112;
			const targetTop =
				preguntasRef.current.getBoundingClientRect().top +
				window.pageYOffset -
				navbarOffset;

			window.scrollTo({
				top: Math.max(0, targetTop),
				behavior: "smooth",
			});
		}

		if (preguntas && currentQuestion) {
			if (!questionTimersRef.current[currentQuestion.question_id]) {
				questionTimersRef.current[currentQuestion.question_id] = Date.now();
			}

			const pageIndex = Math.floor(Math.max(0, currentQuestionNumber - 1) / 28);
			setPaginaNavegador(pageIndex);
		}
	}, [bloquActual, preguntas, currentQuestion, currentQuestionNumber]);

	const handleSeleccionarRespuesta = async (questionId, optionId) => {
		if (answerResults[questionId]) return;

		setIsLoadingExplanation(true);

		setRespuestasSeleccionadas((prev) => ({
			...prev,
			[questionId]: optionId,
		}));

		try {
			const startedAt = questionTimersRef.current[questionId];
			const elapsedSeconds = startedAt
				? Math.max(1, Math.round((Date.now() - startedAt) / 1000))
				: 42;

			const { data, error } = await savePracticeAnswer(
				sessionId,
				questionId,
				optionId,
				elapsedSeconds
			);
			if (error) throw error;

			const question = questionById.get(questionId);
			const normalizedResult = normalizeAnswerResult(data, question);
			normalizedResult.timeSpentSeconds = elapsedSeconds;

			setAnswerResults((prev) => ({
				...prev,
				[questionId]: normalizedResult,
			}));
		} catch (error) {
			showBackdropAlert({
				severity: "warning",
				title: "Respuesta no guardada",
				message: "No se pudo guardar la respuesta.",
				description: "Verifica tu conexión e inténtalo nuevamente.",
			});
		} finally {
			setIsLoadingExplanation(false);
		}
	};

	const handleMarcarPregunta = (questionId) => {
		setPreguntasMarcadas((prev) => ({
			...prev,
			[questionId]: !prev[questionId],
		}));
	};

	function openReportDialog(block, question) {
		if (preguntasReportadas[question.question_id]) return;
		setReportTarget({ block, question });
		setReportComment("");
	}

	function closeReportDialog() {
		if (isSubmittingReport) return;
		setReportTarget(null);
		setReportComment("");
	}

	async function handleSubmitReport() {
		const question = reportTarget?.question;
		const block = reportTarget?.block;
		const trimmedComment = reportComment.trim();

		if (!question || !trimmedComment) return;

		setIsSubmittingReport(true);

		try {
			const selectedOptionId = respuestasSeleccionadas[question.question_id];
			const selectedOption = (question.options || []).find((option) => option.id === selectedOptionId);

			await reportQuestion({
				examId: sessionId,
				questionId: question.question_id,
				clinicalCase: block?.itemType === "clinical_case" ? block.clinicalCase?.description : null,
				questionText: question.question_text,
				options: (question.options || []).map((option) => `${option.position}. ${option.option_text}`),
				selectedOptionText: selectedOption ? `${selectedOption.position}. ${selectedOption.option_text}` : null,
				userComment: trimmedComment,
			});

			setPreguntasReportadas((prev) => ({ ...prev, [question.question_id]: true }));
			showBackdropAlert({
				severity: "success",
				title: "Reporte enviado",
				message: "Gracias por avisarnos.",
				description: "Revisaremos la pregunta lo antes posible.",
				primaryAction: { label: "Aceptar", onClick: closeBackdropAlert },
			});
		} catch (error) {
			showBackdropAlert({
				severity: "error",
				title: "No se pudo enviar",
				message: "No se pudo enviar el reporte.",
				description: "Intenta nuevamente en unos segundos.",
				primaryAction: { label: "Aceptar", onClick: closeBackdropAlert },
			});
		} finally {
			// La ventana de reporte se cierra siempre, exista o no error al enviar.
			setIsSubmittingReport(false);
			setReportTarget(null);
			setReportComment("");
		}
	}

	const goToQuestion = useCallback((position, questionIndexInsideBlock = null) => {
		setBloquActual(position);

		if (!Number.isInteger(questionIndexInsideBlock) || questionIndexInsideBlock < 0) {
			return;
		}

		const targetBlock = preguntas?.[position];
		if (!targetBlock) return;

		const maxIndex = Math.max(0, (targetBlock.questions?.length || 1) - 1);
		const boundedIndex = Math.min(questionIndexInsideBlock, maxIndex);
		const blockKey = targetBlock.blockId || `block-${position}`;

		setQuestionCursorByBlock((prev) => ({
			...prev,
			[blockKey]: boundedIndex,
		}));
	}, [preguntas]);

	const handlePreviousQuestion = useCallback(() => {
		if (!preguntas || preguntas.length === 0) return;
		const block = preguntas[bloquActual];
		if (!block) return;

		if (currentQuestionIndexInsideBlock > 0) {
			const blockKey = block.blockId || `block-${bloquActual}`;
			setQuestionCursorByBlock((prev) => ({
				...prev,
				[blockKey]: currentQuestionIndexInsideBlock - 1,
			}));
			return;
		}

		if (bloquActual <= 0) return;

		const previousBlockIndex = bloquActual - 1;
		const previousBlock = preguntas[previousBlockIndex];
		const previousLastQuestionIndex = Math.max(0, (previousBlock?.questions?.length || 1) - 1);

		goToQuestion(previousBlockIndex, previousLastQuestionIndex);
	}, [preguntas, bloquActual, currentQuestionIndexInsideBlock, goToQuestion]);

	const getNextUnansweredBlock = useCallback(() => {
		for (let i = bloquActual + 1; i < preguntas.length; i++) {
			const hasUnanswered = preguntas[i].questions.some(
				(q) => !respuestasSeleccionadas[q.question_id]
			);
			if (hasUnanswered) {
				return i;
			}
		}

		return preguntas.length - 1;
	}, [bloquActual, preguntas, respuestasSeleccionadas]);

	const requestNextPracticePayload = useCallback(async () => {
		const { data, error } = await getNextPracticeQuestion(sessionId);
		if (error) throw error;

		return Array.isArray(data) ? data[0] : data;
	}, [sessionId]);

	const handleNextQuestion = useCallback(async () => {
		if (isLoadingExplanation) return;
		if (!preguntas || preguntas.length === 0) return;
		if (!preguntas[bloquActual]) return;
		if (!currentQuestion) return;

		if (!isCurrentQuestionSelected) return;

		const block = preguntas[bloquActual];
		const currentBlockQuestionCount = block.questions?.length || 0;

		if (currentQuestionIndexInsideBlock < currentBlockQuestionCount - 1) {
			const blockKey = block.blockId || `block-${bloquActual}`;
			setQuestionCursorByBlock((prev) => ({
				...prev,
				[blockKey]: currentQuestionIndexInsideBlock + 1,
			}));
			return;
		}

		const isCurrentBlockAnswered = preguntas[bloquActual].questions.every(
			(q) => respuestasSeleccionadas[q.question_id]
		);
		if (!isCurrentBlockAnswered) return;

		const isCurrentBlockCorrected = preguntas[bloquActual].questions.every(
			(q) => answerResults[q.question_id]
		);
		if (!isCurrentBlockCorrected && !isInfinite) return;

		const nextBlockIndex = getNextUnansweredBlock();
		if (nextBlockIndex > bloquActual) {
			goToQuestion(nextBlockIndex);
			return;
		}

		if (!isInfinite) return;

		try {
			setIsLoadingMoreItems(true);
			const firstPayload = await requestNextPracticePayload();
			if (!firstPayload?.has_next) {
				showBackdropAlert({
					severity: "info",
					title: "Sin nuevas preguntas",
					message: firstPayload?.message || "Aun no hay nuevas preguntas disponibles.",
					description: "Puedes finalizar cuando lo desees.",
				});
				return;
			}

			const firstItem = normalizeNextPracticeQuestionItem(firstPayload);
			if (!firstItem) {
				showBackdropAlert({
					severity: "warning",
					title: "No se pudo continuar",
					message: "La siguiente pregunta no tuvo un formato valido.",
					description: "Intenta nuevamente o finaliza la práctica.",
				});
				return;
			}

			if (firstPayload?.session) {
				setSessionMeta((prev) => ({
					...prev,
					...firstPayload.session,
					total_questions: Number(
						firstPayload.session?.total_questions || prev?.total_questions || 0
					),
				}));
			}

			const updatedBlocks = appendPracticeItemToBlocks(preguntas, firstItem);
			setPreguntas(updatedBlocks);

			const targetIndex = findNextUnansweredBlockIndex(
				updatedBlocks,
				answerResults,
				bloquActual
			);
			goToQuestion(targetIndex >= 0 ? targetIndex : Math.max(0, updatedBlocks.length - 1));
		} catch (error) {
			console.error("Error loading next practice question:", error);

			const backendMessage =
				error?.message || error?.details || error?.hint || "";
			const invalidDifficultySession =
				typeof backendMessage === "string" &&
				backendMessage.toLowerCase().includes("question_difficulty");
			const invalidSpecialtySettings =
				typeof backendMessage === "string" &&
				backendMessage.toLowerCase().includes("cannot extract elements from a scalar");
			const invalidUnionOrderBy =
				typeof backendMessage === "string" &&
				backendMessage.toLowerCase().includes("invalid union/intersect/except order by clause");

			showBackdropAlert({
				severity: "warning",
				title: "No se pudo continuar",
				message: "No fue posible cargar más preguntas.",
				description: invalidUnionOrderBy
					? "La función SQL get_next_practice_question tiene un ORDER BY inválido sobre UNION. Corrige la RPC en Supabase para continuar en modo indefinido."
					: invalidSpecialtySettings
					? "La sesión guardó specialty_ids con formato inválido. Finaliza esta sesión y crea una nueva para continuar en modo indefinido."
					: invalidDifficultySession
					? "La sesión tiene una dificultad inválida. Finaliza esta sesión y crea una nueva para continuar en modo indefinido."
					: backendMessage || "Intenta nuevamente o finaliza la práctica.",
			});
		} finally {
			setIsLoadingMoreItems(false);
		}
	}, [
		preguntas,
		bloquActual,
		currentQuestion,
		currentQuestionIndexInsideBlock,
		isCurrentQuestionSelected,
		isLoadingExplanation,
		answerResults,
		respuestasSeleccionadas,
		isInfinite,
		getNextUnansweredBlock,
		goToQuestion,
		requestNextPracticePayload,
		showBackdropAlert,
	]);

	useEffect(() => {
		setQuestionCursorByBlock({});
	}, [sessionId]);

	useEffect(() => {
		if (!preguntas || preguntas.length === 0) return;
		if (bloquActual >= 0 && bloquActual < preguntas.length) return;

		setBloquActual(Math.max(0, preguntas.length - 1));
	}, [preguntas, bloquActual]);

	const totalQuestions = preguntas
		? preguntas.reduce((acc, block) => acc + block.questions.length, 0)
		: 0;
	const hasQuestions = Array.isArray(preguntas) && preguntas.length > 0;
	const canGoPrevious = bloquActual > 0 || currentQuestionIndexInsideBlock > 0;
	const hasNextQuestionInCurrentBlock =
		(currentBlock?.questions?.length || 0) > 0 &&
		currentQuestionIndexInsideBlock < (currentBlock?.questions?.length || 0) - 1;
	const shouldShowNextButton =
		hasNextQuestionInCurrentBlock || isInfinite || bloquActual < (preguntas?.length || 0) - 1;
	const answeredQuestionsCount = flattenedQuestions.reduce(
		(acc, item) => acc + (answerResults[item.question.question_id] ? 1 : 0),
		0
	);
	const scoredSummary = flattenedQuestions.reduce(
		(acc, item) => {
			const result = answerResults[item.question.question_id];
			const normalizedStatus = normalizeAnswerStatus(result);

			if (normalizedStatus === "correct") {
				acc.correct += 1;
				acc.scored += 1;
			} else if (normalizedStatus === "incorrect") {
				acc.incorrect += 1;
				acc.scored += 1;
			}

			return acc;
		},
		{ correct: 0, incorrect: 0, scored: 0 }
	);
	const correctAnswersCount = scoredSummary.correct;
	const incorrectAnswersCount = scoredSummary.incorrect;
	const scoredQuestionsCount = scoredSummary.scored;
	const unansweredQuestionsCount = Math.max(0, flattenedQuestions.length - answeredQuestionsCount);
	const answeredResponseTimeSeconds = flattenedQuestions.reduce((acc, item) => {
		const result = answerResults[item.question.question_id];
		return acc + (Number(result?.timeSpentSeconds) || 0);
	}, 0);
	const currentAccuracyPercentage =
		scoredQuestionsCount > 0
			? (correctAnswersCount / scoredQuestionsCount) * 100
			: 0;
	const averageResponseTimeSeconds =
		answeredQuestionsCount > 0 ? answeredResponseTimeSeconds / answeredQuestionsCount : 0;
	const progressSummary = useMemo(
		() => ({
			accuracy_percentage: currentAccuracyPercentage,
			total_correct: correctAnswersCount,
			total_answered: answeredQuestionsCount,
		}),
		[currentAccuracyPercentage, correctAnswersCount, answeredQuestionsCount]
	);
	const visibleNavigatorCount = isInfinite
		? Math.min(flattenedQuestions.length, Math.max(answeredQuestionsCount + 1, currentQuestionNumber))
		: flattenedQuestions.length;
	const firstUnansweredVisibleIndex = useMemo(() => {
		for (let i = 0; i < visibleNavigatorCount; i++) {
			if (!answerResults[flattenedQuestions[i]?.question?.question_id]) {
				return i + 1;
			}
		}

		return null;
	}, [visibleNavigatorCount, answerResults, flattenedQuestions]);

	return (
		<>
			{hasQuestions && (
				<div className="w-full lg:hidden px-0 mt-0 mb-4">
					<div className="grid grid-cols-1 gap-3">
						<div className="w-full bg-white shadow-md rounded-lg border border-gray-200 p-3">
							<h3 className="text-base font-bold mb-2 text-gray-800">Resumen rapido</h3>

							<div className="flex flex-wrap justify-between items-center gap-2">
								<div className="flex flex-col items-center gap-0.5">
									<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#86efac" }}>
										<svg className="w-5 h-5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
											<path
												fillRule="evenodd"
												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
									<p className="text-xs text-gray-600">Correctas</p>
									<p className="text-base font-bold text-gray-500">{correctAnswersCount}</p>
								</div>

								<div className="flex flex-col items-center gap-0.5">
									<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#fecaca" }}>
										<XCircle size={18} className="text-red-700" />
									</div>
									<p className="text-xs text-gray-600">Incorrectas</p>
									<p className="text-base font-bold text-gray-500">{incorrectAnswersCount}</p>
								</div>

								{!isInfinite && (
									<div className="flex flex-col items-center gap-0.5">
										<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#e5e7eb" }}>
											<svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
												<path
													fillRule="evenodd"
													d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
										<p className="text-xs text-gray-600">Sin responder</p>
										<p className="text-base font-bold text-gray-500">{unansweredQuestionsCount}</p>
									</div>
								)}

								<div className="flex flex-col items-center gap-0.5">
									<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#eef2ff" }}>
										<Timer size={14} className="text-indigo-600" />
									</div>
									<p className="text-xs text-gray-600">Tiempo</p>
									<p className="text-base font-bold text-indigo-600">{formatElapsedTime(elapsedSeconds)}</p>
								</div>
							</div>
						</div>

						<div className="w-full">
							<ProgresoGeneral
								summary={progressSummary}
								title="Progreso de la sesion"
								trendSuffix=""
								trendLabel="Tiempo de respuesta"
								trendLabelBelow={true}
								showTrendSign={false}
								weeklyDeltaPercentage={averageResponseTimeSeconds}
							/>
						</div>
					</div>
				</div>
			)}

			<div className="flex flex-wrap mt-100 items-stretch">
				<div className="w-full lg:w-8/12 px-0 lg:px-2 flex flex-col gap-4">
				<div
					ref={preguntasRef}
					className="bg-white shadow-md rounded-lg border border-gray-200 p-4 flex flex-col"
				>
					<div className="w-full mb-4">
						<div className="flex items-center justify-between">
							{preguntas && preguntas.length > 0 && (
								<div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
									<h1 className="text-lg font-bold flex items-center whitespace-nowrap md:flex-shrink-0">
										<ScrollText size={24} color="#6366f1" className="mr-2 flex-shrink-0" />
										{isInfinite
											? `Pregunta ${currentQuestionNumber}`
											: `Pregunta ${currentQuestionNumber} de ${totalQuestions}`}
									</h1>
									<div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-end md:gap-3">
										<Stack
											direction="row"
											spacing={1}
											sx={{
												width: "100%",
												flexWrap: "wrap",
												alignItems: "center",
												justifyContent: { xs: "center", md: "flex-end" },
											}}
										>
											<Chip
													label={`Dificultad: ${getDifficultyLabel(currentQuestion?.difficulty).label}`}
												variant="outlined"
												sx={{
												padding: "8px 10px",
												fontSize: "0.75rem",
												color: getDifficultyLabel(currentQuestion?.difficulty).color,
												borderColor: getDifficultyLabel(currentQuestion?.difficulty).border,
												"& .MuiChip-label": {
													color: getDifficultyLabel(currentQuestion?.difficulty).color,
												},
											}}
											/>
											<Chip
												label={currentQuestion?.specialty_name || "Especialidad"}
												color="secondary"
												variant="outlined"
												icon={<Stethoscope size={18} />}
												sx={{ padding: "8px 10px", fontSize: "0.75rem" }}
											/>
											<Chip
												label={`Tiempo: ${formatElapsedTime(elapsedSeconds)}`}
												color="primary"
												variant="outlined"
												icon={<Timer size={16} />}
												sx={{
													padding: "8px 10px",
													fontSize: "0.75rem",
													width: "auto",
													minWidth: 118,
													flexShrink: 0,
												}}
											/>
										</Stack>
									</div>
								</div>
							)}
						</div>
					</div>

					<div className="w-full mb-4">
						<div className="flex items-center justify-between">
							{isLoadingSession ? (
								<div className="flex min-h-[420px] w-full items-center justify-center p-4">
									<div className="flex flex-col items-center gap-4 rounded-2xl bg-white px-8 py-10 shadow-sm">
										<CircularProgress size={50} sx={{ color: "#6366f1" }} />
										<p className="text-sm font-medium text-gray-600">Cargando sesión...</p>
									</div>
								</div>
							) : preguntas && preguntas.length > 0 ? (
								<div className="w-full">
									{(() => {
										const block = preguntas[bloquActual];
										if (!block) {
											return (
												<div className="flex min-h-[160px] w-full items-center justify-center p-4 text-gray-500">
													Cargando siguiente pregunta...
												</div>
											);
										}
										const blockImage = resolveBlockImage(block);
										const hideBrokenImage = Boolean(brokenImagesByBlock[block.blockId]);
										const imageAspectRatio = imageAspectRatioByBlock[block.blockId] || null;
										const isPortraitImage = Number(imageAspectRatio) > 0 && imageAspectRatio < 1;
										return (
											<div key={block.blockId} className="space-y-4">
												{block.itemType === "clinical_case" && (
															<section className="rounded-2xl border bg-white p-3 lg:p-5">
														<h2 className="text-xl font-bold">Caso clínico</h2>

														<div className="mt-3">
															<div className="mb-4">
																<p className="whitespace-pre-line text-gray-700 leading-relaxed">
																	{block.clinicalCase?.description}
																</p>
															</div>

																{blockImage && !hideBrokenImage && (
																<div className="flex justify-center">
																	<div className="inline-block max-w-full rounded-xl border-2 border-gray-300 bg-gray-50 p-2 shadow-md">
																	<img
																			src={blockImage.src}
																			alt={blockImage.alt}
																			onLoad={(event) => {
																				const { naturalWidth, naturalHeight } = event.currentTarget;
																				if (!naturalWidth || !naturalHeight) return;

																				setImageAspectRatioByBlock((prev) => ({
																					...prev,
																					[block.blockId]: naturalWidth / naturalHeight,
																				}));
																			}}
																			onError={() => {
																				setBrokenImagesByBlock((prev) => ({
																					...prev,
																					[block.blockId]: true,
																				}));
																			}}
																			loading="lazy"
																			decoding="async"
																		style={{
																				width: isPortraitImage ? "auto" : "100%",
																			height: "auto",
																				maxWidth: "100%",
																				maxHeight: "72vh",
																			objectFit: "contain",
																		}}
																			className="block max-w-full rounded-lg"
																	/>
																	</div>
																</div>
															)}

															{block.clinicalCase?.labs && Object.keys(block.clinicalCase.labs).length > 0 && (
																<div className="mt-5">
																	<h3 className="text-md font-semibold text-gray-800 mb-3">Laboratorios</h3>
																	<div className="overflow-x-auto">
																		<table className="w-full border-collapse text-sm">
																			<thead>
																				<tr>
																					{Object.entries(block.clinicalCase.labs).map(([key]) => (
																						<th
																							key={key}
																							className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700"
																							style={{ backgroundColor: "#e5e7eb" }}
																						>
																							{key}
																						</th>
																					))}
																				</tr>
																			</thead>
																			<tbody>
																				<tr>
																					{Object.entries(block.clinicalCase.labs).map(([key, value]) => (
																						<td key={key} className="border border-gray-300 px-4 py-2 text-gray-600">
																							{value}
																						</td>
																					))}
																				</tr>
																			</tbody>
																		</table>
																	</div>
																</div>
															)}
														</div>
													</section>
												)}

												{currentQuestion && (() => {
													const question = currentQuestion;
													const result = answerResults[question.question_id];
													const answered = Boolean(result);
													const selected = Boolean(respuestasSeleccionadas[question.question_id]);

													return (
															<section key={question.question_id} className="relative rounded-2xl border bg-white p-3 lg:p-5">
																<div style={{ position: "absolute", top: 16, left: 16, right: 16 }} className="flex items-center justify-between">
																<button
																	onClick={() => openReportDialog(block, question)}
																	disabled={Boolean(preguntasReportadas[question.question_id])}
																	className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
																	title={preguntasReportadas[question.question_id] ? "Pregunta reportada" : "Reportar pregunta"}
																>
																	<Flag
																		size={22}
																		className={
																			preguntasReportadas[question.question_id]
																				? "text-red-500 fill-current"
																				: "text-gray-400 hover:text-red-500"
																		}
																	/>
																	<span>Reportar pregunta</span>
																</button>
																<button
																	onClick={() => handleMarcarPregunta(question.question_id)}
																		className="p-1 hover:bg-yellow-50 rounded transition-colors"
																	title="Marcar pregunta"
																>
																	<svg
																		className={`w-6 h-6 ${
																			preguntasMarcadas[question.question_id]
																				? "text-yellow-500 fill-current"
																				: "text-gray-400"
																		}`}
																		fill="currentColor"
																		viewBox="0 0 20 20"
																	>
																		<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
																	</svg>
																</button>
															</div>

																<p className="pt-10 lg:pt-6 pr-2 font-semibold leading-relaxed mb-2">
																	{capitalizeFirstLetter(question.question_text)}
																</p>

															<div className="mt-4 space-y-2">
																{(question.options || []).map((option) => {
																	const isSelected =
																		respuestasSeleccionadas[question.question_id] === option.id;
																	const isHovered =
																		hoverOption === `${question.question_id}-${option.id}`;
																	const isCorrectOption =
																		answered && result?.correctOptionId === option.id;
																	const isWrongSelected =
																		answered && isSelected && !result?.isCorrect;

																	let dynamicStyle = {
																		border: "1px solid #d1d5db",
																		backgroundColor: "white",
																	};

																	if (answered && isCorrectOption) {
																		dynamicStyle = {
																			border: "2px solid #16a34a",
																			backgroundColor: "#f0fdf4",
																		};
																	} else if (answered && isWrongSelected) {
																		dynamicStyle = {
																			border: "2px solid #ef4444",
																			backgroundColor: "#fef2f2",
																		};
																											} else if (!answered && (isSelected || isHovered)) {
																		dynamicStyle = {
																			border: "2px solid #6366f1",
																			backgroundColor: "#f0f4ff",
																		};
																	}

																	return (
																		<button
																			key={option.id}
																			type="button"
																			onClick={() =>
																				handleSeleccionarRespuesta(question.question_id, option.id)
																			}
																			onMouseEnter={() =>
																				setHoverOption(`${question.question_id}-${option.id}`)
																			}
																			onMouseLeave={() => setHoverOption(null)}
																			style={{
																				...dynamicStyle,
																				outline: "none",
																				boxShadow: "none",
																			}}
																											disabled={selected}
																			className="block w-full rounded-xl p-3 text-left transition-all focus:outline-none disabled:cursor-default"
																		>
																																			{option.position}. {capitalizeFirstLetter(option.option_text)}
																		</button>
																	);
																})}
															</div>

															{answered && (
																<Paper
																	elevation={0}
																	sx={{
																		mt: 2,
																		borderRadius: 2,
																		border: "1px solid",
																		borderColor: result?.isCorrect ? "#86efac" : "#fecaca",
																		bgcolor: result?.isCorrect ? "#f0fdf4" : "#fef2f2",
																		p: 2,
																	}}
																>
																	<Stack direction="row" alignItems="center" spacing={1}>
																		{result?.isCorrect ? (
																			<CheckCircle2 size={18} color="#15803d" />
																		) : (
																			<XCircle size={18} color="#b91c1c" />
																		)}
																		<Typography
																			sx={{
																				fontSize: "0.92rem",
																				fontWeight: 700,
																				color: result?.isCorrect ? "#166534" : "#991b1b",
																			}}
																		>
																			{result?.isCorrect ? "Respuesta correcta" : "Respuesta incorrecta"}
																		</Typography>
																	</Stack>

																	{result?.explanation && (
																		<>
																			<Divider sx={{ my: 1.5 }} />
																			<Stack direction="row" spacing={1} alignItems="flex-start">
																				<Lightbulb size={16} color="#4f46e5" style={{ marginTop: 2 }} />
																				<Box>
																					<Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#4338ca", mb: 0.5 }}>
																					Explicacion
																				</Typography>
																					<Typography sx={{ fontSize: "0.85rem", color: "#334155", whiteSpace: "pre-line", lineHeight: 1.5 }}>
																						{result.explanation}
																					</Typography>
																				</Box>
																			</Stack>
																		</>
																	)}

																	{(result?.source || result?.sourceUrl) && (
																		<>
																			<Divider sx={{ my: 1.5 }} />
																			<Stack spacing={1} sx={{ width: "100%" }}>
																				<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
																					<Box sx={{ display: "inline-flex", alignItems: "center", minHeight: 32 }}>
																						<BookOpen size={16} color="#0f766e" />
																					</Box>
																					<Typography
																						sx={{
																							fontSize: "0.82rem",
																							color: "#0f172a",
																							fontWeight: 600,
																							lineHeight: 1.2,
																							display: "flex",
																							alignItems: "center",
																							minHeight: 32,
																						}}
																					>
																						{result?.source || "Referencia"}
																					</Typography>
																				</Stack>
																				{result?.sourceUrl && (
																					<Box sx={{ pl: 3 }}>
																						<Button
																							size="small"
																							variant="text"
																							onClick={() => window.open(result.sourceUrl, "_blank", "noopener,noreferrer")}
																							sx={{
																								textTransform: "none",
																								fontWeight: 600,
																								minWidth: 118,
																								height: 32,
																								px: 1,
																							}}
																						>
																							<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
																								Ver fuente
																								<ExternalLink size={14} />
																							</Box>
																						</Button>
																					</Box>
																				)}
																			</Stack>
																		</>
																	)}
																</Paper>
															)}
														</section>
													);
												})()}

												<div className="flex justify-end gap-3 mt-6">
													{canGoPrevious && (
														<Button
															variant="outlined"
															onClick={handlePreviousQuestion}
															startIcon={<ArrowLeft size={20} />}
															sx={{ color: "#6366f1", borderColor: "#6366f1", marginRight: "auto" }}
														>
															Anterior
														</Button>
													)}

													{shouldShowNextButton && (
														<Button
															variant="contained"
															endIcon={<ArrowRight size={20} />}
															onClick={handleNextQuestion}
															disabled={
																isLoadingMoreItems ||
																				isLoadingExplanation ||
																!isCurrentQuestionSelected
															}
															sx={{ backgroundColor: "#6366f1" }}
														>
																			{isLoadingExplanation
																				? "Cargando explicación..."
																			: isLoadingMoreItems
																			? "Cargando..."
																			: "Siguiente"}
														</Button>
													)}

													{!isInfinite && bloquActual === preguntas.length - 1 && (
														<Button
															variant="contained"
																endIcon={<ArrowRight size={20} />}
																onClick={handleFinishSession}
															disabled={
																isFinishing ||
																!preguntas[bloquActual].questions.every((q) => answerResults[q.question_id])
															}
																sx={{ backgroundColor: "#6366f1" }}
														>
																{isFinishing ? "Finalizando..." : "Finalizar"}
														</Button>
													)}
												</div>

											</div>
										);
									})()}
								</div>
							) : (
								<div className="flex min-h-[240px] w-full items-center justify-center p-4 text-gray-500">
									No hay preguntas disponibles para esta sesión.
								</div>
							)}
						</div>
					</div>
				</div>

				{hasQuestions && (isInfinite || bloquActual === preguntas.length - 1) && (
					<div className="hidden lg:block bg-white shadow-md rounded-lg border border-gray-200 p-3">
						<Button
							fullWidth
							variant="contained"
							startIcon={<Square size={14} />}
							onClick={handleFinishSession}
							disabled={isFinishing}
							sx={{
								backgroundColor: "#ef4444",
								"&:hover": { backgroundColor: "#dc2626" },
							}}
						>
							{isFinishing ? "Finalizando..." : "Finalizar Practica"}
						</Button>
					</div>
				)}
				</div>

				{hasQuestions && (isInfinite || bloquActual === preguntas.length - 1) && (
					<div className="w-full lg:hidden px-0 mt-4 mb-4">
						<div className="bg-white shadow-md rounded-lg border border-gray-200 p-3">
							<Button
								fullWidth
								variant="contained"
								startIcon={<Square size={14} />}
								onClick={handleFinishSession}
								disabled={isFinishing}
								sx={{
									backgroundColor: "#ef4444",
									"&:hover": { backgroundColor: "#dc2626" },
								}}
							>
								{isFinishing ? "Finalizando..." : "Finalizar Practica"}
							</Button>
						</div>
					</div>
				)}

				<div className="w-full lg:w-4/12 px-0 lg:px-4 lg:pl-6">
					<div className="hidden lg:block bg-white shadow-md rounded-lg border border-gray-200 p-3 mb-4">
						<h3 className="text-base font-bold mb-2 text-gray-800">Resumen rápido</h3>

							<div className="flex flex-wrap justify-between items-center gap-2">
								<div className="flex flex-col items-center gap-0.5">
								<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#86efac" }}>
									<svg className="w-5 h-5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
									<p className="text-xs text-gray-600">Correctas</p>
								<p className="text-base font-bold text-gray-500">
										{correctAnswersCount}
								</p>
								</div>

								<div className="flex flex-col items-center gap-0.5">
									<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#fecaca" }}>
										<XCircle size={18} className="text-red-700" />
									</div>
									<p className="text-xs text-gray-600">Incorrectas</p>
								<p className="text-base font-bold text-gray-500">
										{incorrectAnswersCount}
								</p>
								</div>

								{!isInfinite && (
									<div className="flex flex-col items-center gap-0.5">
										<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#e5e7eb" }}>
											<svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
												<path
													fillRule="evenodd"
													d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
										<p className="text-xs text-gray-600">Sin responder</p>
										<p className="text-base font-bold text-gray-500">{unansweredQuestionsCount}</p>
									</div>
								)}

								<div className="flex flex-col items-center gap-0.5">
								<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#eef2ff" }}>
									<Timer size={14} className="text-indigo-600" />
								</div>
								<p className="text-xs text-gray-600">Tiempo</p>
								<p className="text-base font-bold text-indigo-600">
									{formatElapsedTime(elapsedSeconds)}
								</p>
							</div>
							</div>
					</div>

					<div className="hidden lg:block mb-4">
							<ProgresoGeneral
								summary={progressSummary}
								title="Progreso de la sesion"
								trendSuffix=""
								trendLabel="Tiempo de respuesta"
								trendLabelBelow={true}
								showTrendSign={false}
								weeklyDeltaPercentage={averageResponseTimeSeconds}
							/>
					</div>

					<div className="bg-white shadow-md rounded-lg border border-gray-200 p-4 mb-4 mt-4 lg:mt-0">
						<h3 className="text-lg font-bold mb-3 text-gray-800">Navegador de preguntas</h3>

						<div className="flex flex-wrap gap-x-2 gap-y-1 mb-3">
							<div className="flex items-center gap-2">
								<div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#86efac" }}></div>
								<span className="text-xs text-gray-600 mr-2">Correcta</span>
							</div>
							<div className="flex items-center gap-2">
									<div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#fecaca" }}></div>
								<span className="text-xs text-gray-600">Incorrecta</span>
							</div>
							<div className="flex items-center gap-2">
								<div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#fcd34d" }}></div>
								<span className="text-xs text-gray-600">Marcada</span>
							</div>
							{!isInfinite && (
								<div className="flex items-center gap-2">
									<div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#e5e7eb" }}></div>
									<span className="text-xs text-gray-600">Sin responder</span>
								</div>
							)}
						</div>

						<div className="overflow-x-auto">
							<table className="w-full">
								<tbody>
									{preguntas &&
										Array.from({ length: 4 }).map((_, rowIndex) => {
											const actualRowIndex = paginaNavegador * 4 + rowIndex;
											return (
												<tr key={`row-${actualRowIndex}`} className="flex gap-2 mb-2">
													{Array.from({ length: 7 }).map((_, colIndex) => {
														const globalIndex = actualRowIndex * 7 + colIndex + 1;
														const item = flattenedQuestions[globalIndex - 1];

														if (globalIndex > visibleNavigatorCount || !item) {
															return <td key={`empty-${actualRowIndex}-${colIndex}`} className="flex-1"></td>;
														}

																	const { question, blockIndex, questionIndexInsideBlock } = item;
														const isAnswered = Boolean(answerResults[question.question_id]);
														const isIncorrect = Boolean(answerResults[question.question_id] && !answerResults[question.question_id]?.isCorrect);
														const isMarked = Boolean(preguntasMarcadas[question.question_id]);
																	const isCurrent =
																		blockIndex === bloquActual &&
																		questionIndexInsideBlock === currentQuestionIndexInsideBlock;
														const isFirstUnanswered = globalIndex === firstUnansweredVisibleIndex;

														let bgColor = "#e5e7eb";
														if (isIncorrect) {
															bgColor = "#fecaca";
														} else if (isAnswered) {
															bgColor = "#86efac";
														}
														if (isMarked) {
															bgColor = "#fcd34d";
														}

														return (
															<td key={`${actualRowIndex}-${colIndex}`} className="flex-1">
																<button
																				onClick={() => goToQuestion(blockIndex, questionIndexInsideBlock)}
																	disabled={!isAnswered && !isFirstUnanswered}
																	className="w-full aspect-square rounded-2xl font-bold text-sm transition-all flex items-center justify-center border-2 disabled:cursor-not-allowed disabled:opacity-50"
																	style={{
																		backgroundColor: isCurrent ? "#eef2ff" : bgColor,
																		color: isCurrent ? "#4f46e5" : "#1f2937",
																		borderColor: isCurrent ? "#4f46e5" : "#e5e7eb",
																	}}
																>
																	{globalIndex}
																</button>
															</td>
														);
													})}
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>

						{preguntas && Math.ceil(visibleNavigatorCount / 28) > 1 && (
							<div className="flex justify-between items-center mt-4">
								<button
									onClick={() => setPaginaNavegador(Math.max(0, paginaNavegador - 1))}
									disabled={paginaNavegador === 0}
									className="px-3 py-1 text-sm bg-indigo-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
								>
									Anterior
								</button>
								<span className="text-xs text-gray-600">
									Página {paginaNavegador + 1} de {Math.ceil(visibleNavigatorCount / 28)}
								</span>
								<button
									onClick={() =>
										setPaginaNavegador(
											Math.min(Math.ceil(visibleNavigatorCount / 28) - 1, paginaNavegador + 1)
										)
									}
									disabled={paginaNavegador === Math.ceil(visibleNavigatorCount / 28) - 1}
									className="px-3 py-1 text-sm bg-indigo-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
								>
									Siguiente
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<BackdropAlert
				open={backdropAlert.open}
				onClose={closeBackdropAlert}
				severity={backdropAlert.severity}
				title={backdropAlert.title}
				message={backdropAlert.message}
				description={backdropAlert.description}
				closeOnBackdropClick={backdropAlert.closeOnBackdropClick}
				primaryAction={
					backdropAlert.primaryAction || {
						label: "Aceptar",
						onClick: closeBackdropAlert,
					}
				}
				secondaryAction={backdropAlert.secondaryAction}
			/>

			<Dialog open={Boolean(reportTarget)} onClose={closeReportDialog} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
				<DialogTitle>Reportar pregunta</DialogTitle>
				<DialogContent>
					<p className="text-sm text-gray-600 mb-3">
						Cuéntanos qué está mal redactado o incorrecto en esta pregunta. Enviaremos el detalle a soporte.
					</p>
					<TextField
						autoFocus
						multiline
						minRows={4}
						fullWidth
						placeholder="Describe el problema..."
						value={reportComment}
						onChange={(event) => setReportComment(event.target.value)}
						disabled={isSubmittingReport}
						sx={{
							// Tailwind's forms reset styles the raw textarea with its own border/ring, doubling MUI's outline.
							"& .MuiInputBase-input, & .MuiInputBase-input:focus": {
								border: "none",
								boxShadow: "none",
							},
						}}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button onClick={closeReportDialog} disabled={isSubmittingReport}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmitReport}
						disabled={isSubmittingReport || !reportComment.trim()}
						sx={{ backgroundColor: "#6366f1" }}
					>
						{isSubmittingReport ? "Enviando..." : "Enviar reporte"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
